import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { fetchCheckoutStatus } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function Success() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("polling"); // polling | paid | expired | error
    const [result, setResult] = useState(null);
    const { clearCart } = useCart();
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState("error");
            return;
        }
        let attempts = 0;
        let timer;

        const poll = async () => {
            try {
                const data = await fetchCheckoutStatus(sessionId);
                setResult(data);
                if (data.payment_status === "paid") {
                    setState("paid");
                    if (!cleared.current) {
                        clearCart();
                        cleared.current = true;
                    }
                    return;
                }
                if (data.status === "expired") {
                    setState("expired");
                    return;
                }
                attempts += 1;
                if (attempts >= 8) {
                    setState("error");
                    return;
                }
                timer = setTimeout(poll, 2500);
            } catch {
                attempts += 1;
                if (attempts >= 8) {
                    setState("error");
                    return;
                }
                timer = setTimeout(poll, 2500);
            }
        };

        poll();
        return () => clearTimeout(timer);
    }, [sessionId, clearCart]);

    return (
        <div
            className="bg-cream min-h-[80vh] flex items-center justify-center px-6"
            data-testid="success-page"
        >
            <div className="max-w-xl w-full text-center">
                {state === "polling" && (
                    <div data-testid="success-polling">
                        <Loader2
                            size={48}
                            className="mx-auto text-gold animate-spin"
                        />
                        <h1 className="font-serif text-4xl text-forest mt-6">
                            Curing your order…
                        </h1>
                        <p className="mt-4 text-brown">
                            Confirming your payment with our studio. One moment.
                        </p>
                    </div>
                )}
                {state === "paid" && (
                    <div data-testid="success-paid">
                        <CheckCircle2
                            size={56}
                            className="mx-auto text-gold"
                        />
                        <span className="divider-rule text-brown mt-8">
                            Mahalo!
                        </span>
                        <h1 className="font-serif text-5xl md:text-6xl text-forest mt-4 leading-[1.02]">
                            Order <span className="italic text-gold">glowing.</span>
                        </h1>
                        <p className="mt-5 text-brown leading-relaxed">
                            Your bars are queued for wrapping and island
                            shipping. A receipt is on its way.
                        </p>
                        {result && (
                            <div className="mt-8 p-6 bg-cream-warm rounded-sm border border-forest/10 inline-block text-left">
                                <p className="text-[0.65rem] tracking-[0.3em] uppercase text-brown">
                                    Total Captured
                                </p>
                                <p className="font-serif text-3xl text-forest mt-1">
                                    ${Number(result.amount_total).toFixed(2)}{" "}
                                    <span className="text-sm text-brown">
                                        {result.currency?.toUpperCase()}
                                    </span>
                                </p>
                                {result.order_id && (
                                    <p className="mt-2 text-xs text-brown">
                                        Order №{" "}
                                        <span className="font-mono">
                                            {result.order_id.slice(0, 8)}
                                        </span>
                                    </p>
                                )}
                                {result.gift_note && (
                                    <p className="mt-4 text-sm text-forest italic" data-testid="success-gift-note">
                                        Gift note: “{result.gift_note}”
                                    </p>
                                )}
                                {result.items?.length > 0 && (
                                    <ul className="mt-4 space-y-1 text-sm text-forest" data-testid="success-itemized">
                                        {result.items.map((it, i) => (
                                            <li key={i} className="flex justify-between gap-6">
                                                <span>
                                                    {it.qty} × {it.name}
                                                    <span className="block text-xs text-brown">
                                                        Sold by {it.seller_name || "Island Gold Glow Soap Co."}
                                                    </span>
                                                </span>
                                                <span>${Number(it.line_total).toFixed(2)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                        <div className="mt-10 flex flex-wrap gap-3 justify-center">
                            <Link
                                to="/shop"
                                className="btn-pill btn-primary"
                                data-testid="success-continue-btn"
                            >
                                Continue shopping
                            </Link>
                            <Link to="/craft" className="btn-pill btn-ghost">
                                Visit the Studio
                            </Link>
                        </div>
                    </div>
                )}
                {state === "expired" && (
                    <div data-testid="success-expired">
                        <XCircle size={48} className="mx-auto text-forest" />
                        <h1 className="font-serif text-4xl text-forest mt-6">
                            Session expired
                        </h1>
                        <p className="mt-4 text-brown">
                            Your checkout session timed out. Nothing was
                            charged.
                        </p>
                        <Link
                            to="/cart"
                            className="btn-pill btn-primary mt-8 inline-flex"
                        >
                            Back to basket
                        </Link>
                    </div>
                )}
                {state === "error" && (
                    <div data-testid="success-error">
                        <XCircle size={48} className="mx-auto text-forest" />
                        <h1 className="font-serif text-4xl text-forest mt-6">
                            Check your inbox
                        </h1>
                        <p className="mt-4 text-brown">
                            We couldn't confirm live. If payment went through,
                            a receipt will arrive shortly.
                        </p>
                        <Link
                            to="/shop"
                            className="btn-pill btn-primary mt-8 inline-flex"
                        >
                            Return to Shop
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
